import type {
    EntityPatch,
    HistoryDomainAdapter,
    HistoryEntitySnapshot,
    HistoryPatchApplyInput,
} from '..';
import type {FeatureRollbackHistorySnapshot} from '../../feature/rollback';

/**
 * FeatureManager 退回控制棒历史 adapter ID。
 */
export const FEATURE_ROLLBACK_HISTORY_ADAPTER_ID = 'history-domain:feature-rollback';

/**
 * FeatureManager 退回控制棒历史恢复端口。
 */
export interface FeatureRollbackHistoryStore {
    /**
     * 暂存退回控制棒历史恢复目标。
     *
     * @param snapshot 退回控制棒历史快照。
     */
    readonly stageFeatureRollbackHistoryRestore: (snapshot: FeatureRollbackHistorySnapshot) => void;
}

/**
 * 判断值是否为有效的 FeatureManager 退回控制棒历史快照。
 *
 * @param value 待判断值。
 * @returns 是否为有效快照。
 */
function isFeatureRollbackHistorySnapshot(value: unknown): value is FeatureRollbackHistorySnapshot {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const snapshot = value as FeatureRollbackHistorySnapshot;
    return typeof snapshot.documentId === 'string'
        && Array.isArray(snapshot.commands)
        && Number.isFinite(snapshot.cursorIndex)
        && (snapshot.previousCursorIndex === null || Number.isFinite(snapshot.previousCursorIndex))
        && Array.isArray(snapshot.timelineItems);
}

/**
 * 应用退回控制棒历史快照。
 *
 * @param input 补丁应用输入。
 */
function applyFeatureRollbackSnapshot(
    input: HistoryPatchApplyInput<FeatureRollbackHistorySnapshot, FeatureRollbackHistoryStore>,
): void {
    if (!input.snapshot.exists) {
        throw new Error(`退回控制棒历史 ${input.patch.entityId} 缺少目标快照。`);
    }
    if (!isFeatureRollbackHistorySnapshot(input.snapshot.data)) {
        throw new Error(`退回控制棒历史 ${input.patch.entityId} 的快照不是可恢复的 plain data。`);
    }

    /**
     * PatchApplier 是同步内核，Viewer replay 是异步操作。
     * 这里仅暂存目标快照，宿主层在历史光标切换成功后统一 flush。
     */
    input.context.stageFeatureRollbackHistoryRestore(structuredClone(input.snapshot.data));
}

/**
 * 比较退回控制棒历史快照。
 *
 * @param left 左侧快照。
 * @param right 右侧快照。
 * @returns 是否语义相等。
 */
function areFeatureRollbackSnapshotsEqual(
    left: HistoryEntitySnapshot<FeatureRollbackHistorySnapshot>,
    right: HistoryEntitySnapshot<FeatureRollbackHistorySnapshot>,
): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

/**
 * 创建 FeatureManager 退回控制棒历史 adapter。
 *
 * @returns 历史领域 adapter。
 */
export function createFeatureRollbackHistoryAdapter(): HistoryDomainAdapter<
    FeatureRollbackHistorySnapshot,
    FeatureRollbackHistoryStore
> {
    return {
        adapterId: FEATURE_ROLLBACK_HISTORY_ADAPTER_ID,
        kind: 'document',
        applySnapshot: applyFeatureRollbackSnapshot,
        areSnapshotsEqual: areFeatureRollbackSnapshotsEqual,
    };
}

/**
 * 创建 FeatureManager 退回控制棒历史补丁。
 *
 * @param patch 补丁字段。
 * @returns 退回控制棒历史补丁。
 */
export function createFeatureRollbackHistoryPatch(
    patch: Omit<EntityPatch<FeatureRollbackHistorySnapshot>, 'adapterId' | 'entityKind'>,
): EntityPatch<FeatureRollbackHistorySnapshot> {
    return {
        ...patch,
        entityKind: 'document',
        adapterId: FEATURE_ROLLBACK_HISTORY_ADAPTER_ID,
    };
}
